'use client'

import { useTranslations } from 'next-intl'

import ModalCloseIcon from '@/assets/icons/modal-close.svg'
import { URLS } from '@/configs/urls'
import type { FanProfileMissingField } from '@/features/tasks/fanProfileCompleteness'
import { useRouter } from '@/i18n/routing'
import Dialog from '@/ui/Dialog'

import S from './TasksCompleteProfileDialog.module.scss'
import {
  TasksCompleteProfileFieldIcon,
  TasksCompleteProfileWarningIcon
} from './TasksCompleteProfileDialogIcons'

type TasksCompleteProfileDialogProps = {
  open: boolean
  missingFields: FanProfileMissingField[]
  onOpenChange: (open: boolean) => void
}

export function TasksCompleteProfileDialog({
  open,
  missingFields,
  onOpenChange
}: TasksCompleteProfileDialogProps) {
  const t = useTranslations('tasksPage.accessDialogs.completeProfile')
  const router = useRouter()

  const handleCompleteProfile = () => {
    onOpenChange(false)
    router.push(URLS.PROFILE_EDIT)
  }

  return (
    <Dialog
      open={open}
      toggle={onOpenChange}
      description={t('description')}
      className={S.dialog}
      withoutClose
    >
      <div className={S.inner}>
        <button
          type="button"
          className={S.cornerClose}
          aria-label={t('closeAria')}
          onClick={() => onOpenChange(false)}
        >
          <ModalCloseIcon className={S.cornerIcon} aria-hidden />
        </button>

        <div className={S.glyph} aria-hidden>
          <TasksCompleteProfileWarningIcon />
        </div>

        <h2 className={S.title}>{t('title')}</h2>
        <p className={S.description}>
          {t('descriptionLine1')}
          <br />
          {t('descriptionLine2')}
        </p>

        <div className={S.fieldList}>
          <p className={S.fieldListLabel}>{t('missingFieldsLabel')}</p>
          <ul className={S.fieldItems}>
            {missingFields.map(field => (
              <li key={field} className={S.fieldItem}>
                <span className={S.fieldIconWrap}>
                  <TasksCompleteProfileFieldIcon field={field} />
                </span>
                <span className={S.fieldLabel}>{t(`fields.${field}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={S.actions}>
          <button
            type="button"
            className={S.primaryBtn}
            onClick={handleCompleteProfile}
          >
            {t('completeProfile')}
          </button>
          <button
            type="button"
            className={S.secondaryBtn}
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
